import { yupResolver } from "@hookform/resolvers/yup";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParticipantListMutation } from "../../../../../api/services/bulkInsertActivity/bulkInsertApis";
import { ReactComponent as ResetAllIcon } from "../../../../../assets/images/reset-all.svg";
import {
  AccordionHeader,
  AccordionHeaderText,
  BulkInsertFooter,
  InformationModal,
  MuiBox,
  MuiButtonPrimary,
  MuiRadioGroup,
  MuiTypography,
  WarningConfirmationModal,
} from "../../../../../components";
import {
  LANGUAGE_CODE,
  PAGE_IDS,
  SOLUTION_OFFERING_CMC_SITECORE_KEY
} from "../../../../../helpers/constants";
import useAccordion from "../../../../../hooks/useAccordion";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import {
  setBulkTransferSelectedParticipants,
  setBulkTransferSelectedStoreParticipants,
} from "../../../../../redux/slices/program/programSlice";
import {
  SolutionOfferingsResponseType,
  TransferSearchParticipantFormKey,
  transferTypeKey,
} from "../../../modelsAndSchemas/Models";
import { BulkTransferSearchParticipantSchema } from "../../../modelsAndSchemas/ValidationSchema";
import { bulkTransferSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import { BulkTransferSearchParticipantsWrapper } from "./BulkTransferSearchParticipants.style";
import { SearchParticipantsForm } from "./searchParticipantsForm/SearchParticipantsForm";
import { SearchParticipantsResult } from "./searchParticipantsResult/SearchParticipantsResult";

import { useGetSolutionOffering } from "../../../../../api/services/programTemplate/programTemplateApis";
import { ButtonWithIcon } from "../../../../../components/buttonWithIcon/ButtonWithIcon";
import { STATUS_BADGE_TYPE } from "../../../../../components/statusBadge/StatusBadge";
import { isNotEmpty, trimValue } from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { bulkTransferSearchInputClickSelector } from "../../../../../redux/slices/program/programSelector";
import { Separator } from "../../../../../styles/common.style";

const pageId = PAGE_IDS.CREATE_BULK_TRANSFER;

const initialAccordionStatus = {
  searchParticipant: true,
  searchResult: true,
  all: true,
};

export interface BulkTransferSearchParticipantsProps {
  onNextButtonClick: () => void;
  currentStep: number;
  handleNavigateBack: () => void;
  selectedTransferType: string;
  setSelectedTransferType: (value: string) => void;
}
export interface BulkTransferSearchParticipantsRef {
  handleResetButton: () => void;
}

export const BulkATransferSearchParticipants = forwardRef<
  BulkTransferSearchParticipantsRef,
  BulkTransferSearchParticipantsProps
>(
  (
    {
      onNextButtonClick,
      currentStep,
      handleNavigateBack,
      selectedTransferType,
      setSelectedTransferType,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const isSearchInputClick = useSelector(
      bulkTransferSearchInputClickSelector,
    );

    const {
      commonValidationMessages,
      commonLabels,
      fetchComponentTitle,
      fetchLabel,
    } = useSiteCore();
    const { commonARIALabel } = useNavigationSiteCore();
    const [showInformationModal, setShowInformationModal] = useState(false);
    const [showWarningConfirmationModal, setShowWarningConfirmationModal] =
      useState(false);

    const { accordionStatus, handleIndividualAccordionChange } = useAccordion(
      initialAccordionStatus,
    );

    const {
      mutateAsync: getParticipantList,
      isPending: isParticipantPending,
      isError: isParticipantError,
      data: participantResults,
    } = useSearchParticipantListMutation();

    const {
      data: solutionOfferingData,
      isError: isSolutionOfferingError,
      isFetching: isSolutionOfferingFetching,
    } = useGetSolutionOffering(LANGUAGE_CODE.EN_US, false, true);

    const defaultFilter = bulkTransferSiteCoreKeys.filterByContains;

    const searchParticipantsDefaultValues = {
      [TransferSearchParticipantFormKey.transferType]: "",
      [TransferSearchParticipantFormKey.deliveryCountry]: "",
      [TransferSearchParticipantFormKey.nextView]: "",
      [TransferSearchParticipantFormKey.productLine]: "",
      [TransferSearchParticipantFormKey.programTemplate]: "",
      [TransferSearchParticipantFormKey.clientFilter]: defaultFilter,
      [TransferSearchParticipantFormKey.client]: "",
    };

    useEffect(() => {
      dispatch(
        toggleLoader({
          isVisible: isSolutionOfferingFetching,
          message: commonLabels.loadingPleaseWait,
        }),
      );
      return () => {
        dispatch(
          toggleLoader({
            isVisible: false,
          }),
        );
      };
    }, [isSolutionOfferingFetching]);

    useEffect(() => {
      if (isParticipantError || isSolutionOfferingError) {
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        });
      }
    }, [isParticipantError, isSolutionOfferingError]);

    useEffect(() => {
      dispatch(
        toggleLoader({
          isParticipantPending: {
            isVisible: isParticipantPending,
          },
        }),
      );
      return () => {
        dispatch(
          toggleLoader({
            isParticipantPending: {
              isVisible: false,
              message: "",
            },
          }),
        );
      };
    }, [isParticipantPending]);

    const renderHeader = (labelKey: string) => {
      return <AccordionHeaderText label={fetchComponentTitle(labelKey)} />;
    };

    const renderResultHeader = () => {
      return (
        <>
          {isSearchInputClick ? (
            renderHeader(bulkTransferSiteCoreKeys.searchParticipantsResultsKey)
          ) : (
            <AccordionHeaderText
              label={fetchLabel(
                bulkTransferSiteCoreKeys.searchParticipantsResultsKey,
                bulkTransferSiteCoreKeys.allParticipants,
              )}
            />
          )}

          <MuiTypography variant="h3Bold" className="selected-count">
            {participantResults?.data?.length ?? 0}
          </MuiTypography>
        </>
      );
    };

    const methods = useForm({
      mode: "all",
      defaultValues: searchParticipantsDefaultValues,
      resolver: yupResolver(
        BulkTransferSearchParticipantSchema(commonValidationMessages),
      ),
    });

    const {
      handleSubmit,
      reset,
      formState: { isDirty },
      getValues,
      clearErrors,
      setValue,
    } = methods;

    const prepareData = () => {
      const {
        deliveryCountry,
        nextView,
        productLine,
        programTemplate,
        client,
        clientFilter,
      } = getValues();

      return {
        deliveryCountryCode: isNotEmpty(deliveryCountry)
          ? [deliveryCountry]
          : [],
        solutionOfferingId: nextView,
        lob: isNotEmpty(productLine) ? [productLine] : [],
        programTemplateId: isNotEmpty(programTemplate) ? [programTemplate] : [],
        clientFilter: clientFilter,
        client: trimValue(client),
        status: STATUS_BADGE_TYPE.active,
      };
    };

    const getParticipantListApi = () => {
      getParticipantList(prepareData());
      dispatch(setBulkTransferSelectedStoreParticipants([]));
    };

    const handleSearchClick = () => {
      handleCommonResetAndSearchClick();
    };

    const getCMCId = () => {
      return (
        solutionOfferingData?.find(
          (item: SolutionOfferingsResponseType) =>
            item.nameKey === SOLUTION_OFFERING_CMC_SITECORE_KEY,
        )?.id ?? ""
      );
    };

    const setSelectedTransferTypeWithNextView = () => {
      if (selectedTransferType === transferTypeKey.changeCountry) {
        setSelectedTransferType(transferTypeKey.changeProgram);
        reset({
          ...searchParticipantsDefaultValues,
          [TransferSearchParticipantFormKey.nextView]: getCMCId(),
        });
      } else {
        setSelectedTransferType(transferTypeKey.changeCountry);
      }
    };

    const handleChangeTransferConfirmation = () => {
      reset(searchParticipantsDefaultValues);
      setShowWarningConfirmationModal(false);
      handleCommonResetAndSearchClick();
      setSelectedTransferTypeWithNextView();
    };

    const handleCommonResetAndSearchClick = () => {
      //directly rest the form and set the default values with api call
      dispatch(setBulkTransferSelectedParticipants([]));
      getParticipantListApi();
    };

    const handleResetButton = () => {
      reset(searchParticipantsDefaultValues);
      handleCommonResetAndSearchClick();
      setSelectedTransferType(transferTypeKey.changeCountry);
    };

    useImperativeHandle(ref, () => ({
      handleResetButton,
    }));

    const handleCloseInformation = () => {
      setShowInformationModal(false);
    };

    const getLabelFromSearchForm = (key: string) => {
      return fetchLabel(bulkTransferSiteCoreKeys.transferTypeKey, key);
    };

    const radioOptions = [
      {
        value: transferTypeKey.changeCountry,
        label: getLabelFromSearchForm(
          bulkTransferSiteCoreKeys.changeCountryRadioButton,
        ),
      },
      {
        value: transferTypeKey.changeProgram,
        label: getLabelFromSearchForm(
          bulkTransferSiteCoreKeys.changeProgramRadioButton,
        ),
      },
    ];

    const handleTransferTypeChange = (
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      if (isDirty) {
        setShowWarningConfirmationModal(true);
      } else {
        clearErrors();
        setValue(TransferSearchParticipantFormKey.nextView, "");
        setSelectedTransferTypeWithNextView();
      }
    };

    return (
      <BulkTransferSearchParticipantsWrapper>
        <MuiBox
          className="accordion-header transfer-accordion"
          data-testid="search-participant"
        >
          <AccordionHeader
            title={renderHeader(bulkTransferSiteCoreKeys.transferTypeKey)}
            isExpanded={true}
          >
            <MuiBox className="transfer-type-container">
              <MuiRadioGroup
                label={getLabelFromSearchForm(
                  bulkTransferSiteCoreKeys.chooseType,
                )}
                value={selectedTransferType}
                onChange={handleTransferTypeChange}
                options={radioOptions}
                requiredARIALabel={commonARIALabel.required}
              />
            </MuiBox>
          </AccordionHeader>
        </MuiBox>
        <MuiBox className="accordion-header">
          <FormProvider {...methods}>
            <AccordionHeader
              title={renderHeader(
                bulkTransferSiteCoreKeys.searchParticipantsKey,
              )}
              onChange={() =>
                handleIndividualAccordionChange("searchParticipant")
              }
              isExpanded={accordionStatus.searchParticipant}
              formKey="searchParticipantForm"
            >
              <MuiBox className="semi-header">
                <ButtonWithIcon
                  data-testid={`${pageId}-clearAll`}
                  buttonText={commonLabels.resetAllSearch}
                  onActionClick={() => handleResetButton()}
                  ActionIcon={ResetAllIcon}
                />
              </MuiBox>
              <Separator />
              <SearchParticipantsForm
                pageId={pageId}
                selectedTransferType={selectedTransferType}
                isParticipantListChanged={isParticipantPending}
                solutionOfferingData={solutionOfferingData}
              />
              <MuiBox className="button-container">
                <MuiButtonPrimary
                  id={`${pageId}-search`}
                  onClick={() => handleSubmit(handleSearchClick)()}
                >
                  {commonLabels.search}
                </MuiButtonPrimary>
              </MuiBox>
            </AccordionHeader>
          </FormProvider>
        </MuiBox>

        <MuiBox className="accordion-header accordion-header-result">
          <AccordionHeader
            title={renderResultHeader()}
            onChange={() => handleIndividualAccordionChange("searchResult")}
            isExpanded={accordionStatus.searchResult}
          >
            <SearchParticipantsResult
              rows={participantResults?.data ?? []}
              pageId={pageId}
              setShowInformationModal={setShowInformationModal}
              currentStep={currentStep}
            />
          </AccordionHeader>
        </MuiBox>
        <BulkInsertFooter
          pageId={pageId}
          onConfirmClick={onNextButtonClick}
          handleNavigateBack={handleNavigateBack}
          isFiledUpdated={isDirty}
          confirmButtonText={commonLabels.next}
        />
        {showInformationModal && (
          <InformationModal
            showModal={showInformationModal}
            handleModalClose={handleCloseInformation}
            handlePrimaryButtonClick={handleCloseInformation}
            message={fetchLabel(
              bulkTransferSiteCoreKeys.searchParticipantsResultsKey,
              bulkTransferSiteCoreKeys.selectSearchParameterErrorMessage,
            )}
          />
        )}

        {showWarningConfirmationModal && (
          <WarningConfirmationModal
            modalTitle={commonLabels.confirmation}
            labelContent={fetchLabel(
              bulkTransferSiteCoreKeys.transferTypeKey,
              bulkTransferSiteCoreKeys.changeTransferTypeConfirmationMessage,
            )}
            show={showWarningConfirmationModal}
            onClose={() => setShowWarningConfirmationModal(false)}
            onPrimaryButtonClick={handleChangeTransferConfirmation}
            primaryButtonTitle={commonLabels.continue}
            secondaryButtonTitle={commonLabels.cancel}
          />
        )}
      </BulkTransferSearchParticipantsWrapper>
    );
  },
);
